/**
 * Modal fullscreen para visualização de tela remota (WebRTC)
 * Expande o viewer para tela cheia com controles overlay
 * 
 * @author Sistema
 * @version 1.1.0
 * 
 * HISTÓRICO DE ALTERAÇÕES:
 * - v1.1.0: Implementado callback ref para attach robusto do stream,
 *           corrigindo bug de tela preta ao expandir modal
 * - v1.0.0: Versão inicial
 */

import { useRef, useEffect, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Dialog, 
  DialogContent,
} from '@/components/ui/dialog';
import { 
  X, 
  Signal, 
  SignalLow, 
  SignalZero,
  Monitor,
  Minimize2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { createLogger } from '@/lib/logger';

// Logger condicional para debugging
const logger = createLogger('ScreenShareModal');

interface ScreenShareModalProps {
  /** Se true, modal está aberto */
  open: boolean;
  /** Callback para fechar modal */
  onOpenChange: (open: boolean) => void;
  /** Stream de vídeo remoto */
  stream: MediaStream | null;
  /** Estado da conexão P2P */
  connectionState: RTCPeerConnectionState;
  /** Callback para encerrar sessão */
  onClose: () => void;
  /** Nome do contato/cliente */
  contactName?: string;
}

/**
 * Função utilitária para anexar stream ao elemento de vídeo
 * Centraliza a lógica de attach + play para evitar duplicação
 * 
 * @param videoElement - Elemento HTMLVideoElement
 * @param stream - MediaStream a ser anexado
 */
const attachStreamToVideo = (
  videoElement: HTMLVideoElement | null, 
  stream: MediaStream | null
): void => {
  if (!videoElement || !stream) return;
  
  // Só anexa se ainda não estiver anexado (evita re-attach desnecessário)
  if (videoElement.srcObject !== stream) {
    videoElement.srcObject = stream;
    logger.debug('Stream anexado ao elemento de vídeo');
  }
  
  // Força play() para garantir que o vídeo inicie
  // Alguns navegadores exigem chamada explícita após srcObject ser definido
  videoElement.play().catch((err) => {
    // Erro comum quando autoplay é bloqueado; não é crítico
    logger.warn('Erro ao iniciar playback (pode ser política de autoplay):', err.message);
  });
};

/**
 * Função utilitária para limpar/desanexar stream do elemento de vídeo
 * Garante cleanup completo evitando "estado zumbi"
 * 
 * @param videoElement - Elemento HTMLVideoElement
 */
const detachStreamFromVideo = (videoElement: HTMLVideoElement | null): void => {
  if (!videoElement) return;
  
  // Pausa antes de limpar para evitar erros
  videoElement.pause();
  videoElement.srcObject = null;
  logger.debug('Stream desanexado do elemento de vídeo');
};

/**
 * Modal fullscreen para visualização expandida de tela remota
 * 
 * Oferece experiência imersiva com:
 * - Vídeo em tela cheia
 * - Controles overlay que aparecem ao hover
 * - Indicador de conexão e timer
 * - Tecla ESC para minimizar
 */
export function ScreenShareModal({
  open,
  onOpenChange,
  stream,
  connectionState,
  onClose,
  contactName = 'Cliente',
}: ScreenShareModalProps) {
  // Ref para armazenar o elemento de vídeo (usado pelo callback ref e effect)
  const videoNodeRef = useRef<HTMLVideoElement | null>(null);
  
  // Estado para duração da sessão
  const [duration, setDuration] = useState(0);
  const durationIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  
  // Estado para mostrar controles (aparecem ao hover)
  const [showControls, setShowControls] = useState(true);
  const hideControlsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Callback ref para o elemento de vídeo
   * 
   * IMPORTANTE: Usando callback ref em vez de useRef + useEffect porque:
   * - Executa IMEDIATAMENTE quando o DOM node é montado pelo Portal do Radix
   * - Evita race conditions onde o useEffect roda antes do ref estar populado
   * - Garante que o stream seja anexado no momento exato da montagem
   */
  const setVideoNode = useCallback((node: HTMLVideoElement | null) => {
    // Cleanup do nó anterior se existir
    if (videoNodeRef.current && videoNodeRef.current !== node) {
      detachStreamFromVideo(videoNodeRef.current);
    }
    
    // Armazena referência
    videoNodeRef.current = node;
    
    // Se temos nó, modal aberto e stream, anexa imediatamente
    if (node && open && stream) {
      attachStreamToVideo(node, stream);
    }
  }, [open, stream]);

  /**
   * Effect de backup para re-attach quando stream ou open mudam
   * Isso cobre casos onde o callback ref já executou mas o stream chegou depois
   */
  useEffect(() => {
    if (open && videoNodeRef.current && stream) {
      attachStreamToVideo(videoNodeRef.current, stream);
    }
    
    // Cleanup quando modal fecha ou stream muda
    return () => {
      if (!open) {
        detachStreamFromVideo(videoNodeRef.current);
      }
    };
  }, [stream, open]);

  // Timer de duração
  useEffect(() => {
    if (open && connectionState === 'connected') {
      durationIntervalRef.current = setInterval(() => {
        setDuration(prev => prev + 1);
      }, 1000);
    }

    return () => {
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
        durationIntervalRef.current = null;
      }
    };
  }, [open, connectionState]);

  // Reset duração quando fecha
  useEffect(() => {
    if (!open) {
      setDuration(0);
    }
  }, [open]);

  // Auto-hide controles após inatividade
  const handleMouseMove = () => {
    setShowControls(true);
    
    if (hideControlsTimeoutRef.current) {
      clearTimeout(hideControlsTimeoutRef.current);
    }
    
    hideControlsTimeoutRef.current = setTimeout(() => {
      setShowControls(false);
    }, 3000);
  };

  // Cleanup timeout
  useEffect(() => {
    return () => {
      if (hideControlsTimeoutRef.current) {
        clearTimeout(hideControlsTimeoutRef.current);
      }
    };
  }, []);

  // Formata duração em MM:SS
  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Ícone de qualidade da conexão
  const getConnectionIcon = () => {
    switch (connectionState) {
      case 'connected':
        return <Signal className="h-4 w-4 text-green-400" />;
      case 'connecting':
        return <SignalLow className="h-4 w-4 text-yellow-400" />;
      default:
        return <SignalZero className="h-4 w-4 text-red-400" />;
    }
  };

  // Handler para encerrar e fechar modal
  const handleClose = () => {
    onClose();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* hideCloseButton=true porque temos controles customizados */}
      <DialogContent 
        className="max-w-[100vw] max-h-[100vh] w-screen h-screen p-0 border-0 bg-black"
        onMouseMove={handleMouseMove}
        hideCloseButton
      >
        {/* Vídeo fullscreen */}
        <div className="relative w-full h-full flex items-center justify-center">
          {/* 
            Vídeo com callback ref para attach robusto
            Usando w-full h-full (igual ao viewer) para evitar sizing issues
          */}
          <video
            ref={setVideoNode}
            autoPlay
            playsInline
            muted
            className="w-full h-full object-contain"
          />

          {/* Overlay de controles (aparece ao hover) */}
          <div
            className={cn(
              'absolute inset-x-0 top-0 p-4 transition-opacity duration-300',
              'bg-gradient-to-b from-black/60 to-transparent',
              showControls ? 'opacity-100' : 'opacity-0'
            )}
          >
            <div className="flex items-center justify-between">
              {/* Info do cliente */}
              <div className="flex items-center gap-3">
                <Monitor className="h-5 w-5 text-white" />
                <span className="text-white font-medium">
                  {contactName}
                </span>
                
                {/* Badge de status */}
                <Badge 
                  variant="secondary"
                  className="bg-white/20 text-white border-0"
                >
                  {getConnectionIcon()}
                  <span className="ml-1.5">
                    {connectionState === 'connected' ? 'Ao vivo' : 'Conectando...'}
                  </span>
                </Badge>
                
                {/* Timer */}
                <span className="text-white/80 font-mono text-sm">
                  {formatDuration(duration)}
                </span>
              </div>

              {/* Botões de controle */}
              <div className="flex items-center gap-2">
                {/* Minimizar */}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 text-white hover:bg-white/20"
                  onClick={() => onOpenChange(false)}
                  title="Minimizar (ESC)"
                >
                  <Minimize2 className="h-5 w-5" />
                </Button>
                
                {/* Encerrar */}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 text-red-400 hover:text-red-300 hover:bg-red-500/20"
                  onClick={handleClose}
                  title="Encerrar visualização"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </div>
          </div>

          {/* Hint de tecla ESC no canto inferior */}
          <div
            className={cn(
              'absolute bottom-4 left-1/2 -translate-x-1/2 transition-opacity duration-300',
              showControls ? 'opacity-100' : 'opacity-0'
            )}
          >
            <span className="text-white/50 text-xs">
              Pressione <kbd className="px-1.5 py-0.5 bg-white/20 rounded text-white/70">ESC</kbd> para minimizar
            </span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
